import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { SelectablePills } from './ui/SelectablePills';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from '../utils/axios.config';
import { BACKEND_CONFIG } from '../utils/productionConfig';
import { Loader, Upload, X, ChevronRight } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { countries, states, cities } from '../utils/locationData';

const API_URL = BACKEND_CONFIG.apiUrl;

interface ProfileCompletionFormProps {
  onComplete: () => void;
  isResubmission?: boolean;
  rejectionReason?: string;
}

export default function ProfileCompletionForm({ onComplete, isResubmission = false, rejectionReason }: ProfileCompletionFormProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { loadUser } = useAuthContext();

  // Basic Info
  const [gender, setGender] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');

  // Location - with dropdown support
  const [country, setCountry] = useState('India');
  const [countryCode, setCountryCode] = useState('IN');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');

  // Education & Career
  const [educationLevel, setEducationLevel] = useState('');
  const [occupation, setOccupation] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');

  // Religion
  const [religion, setReligion] = useState('');

  // Bio
  const [bio, setBio] = useState('');

  // Photos (optional - can be added later)
  const [photos, setPhotos] = useState<string[]>([]);

  // Identity Documents (optional - can be added later)
  const [idDocumentType, setIdDocumentType] = useState('');
  const [idDocumentUrl, setIdDocumentUrl] = useState('');

  // Get available states for selected country
  const getAvailableStates = () => {
    return states[countryCode] || [];
  };

  // Get available cities for selected state
  const getAvailableCities = () => {
    return cities[state] || [];
  };

  // Handle country change
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = countries.find(c => c.name === e.target.value);
    if (selectedCountry) {
      setCountry(selectedCountry.name);
      setCountryCode(selectedCountry.code);
      setState(''); // Reset state when country changes
      setCity(''); // Reset city when country changes
    }
  };

  // Handle state change
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setState(e.target.value);
    setCity(''); // Reset city when state changes
  };

  // Convert image to base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Compress image before upload
  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const maxWidth = 1920;
          const maxHeight = 1920;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Dynamic quality based on file size
          const fileSizeMB = file.size / (1024 * 1024);
          let quality = 0.85;
          if (fileSizeMB > 2) {
            quality = 0.4;
          } else if (fileSizeMB > 1) {
            quality = 0.5;
          } else if (fileSizeMB > 0.5) {
            quality = 0.7;
          }

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Image load failed'));
      };
      reader.onerror = () => reject(new Error('File read failed'));
    });
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (photos.length + files.length > 6) {
      toast.error('Maximum 6 images allowed');
      return;
    }

    try {
      const newImages: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        const compressedFile = await compressImage(file);
        const base64 = await convertToBase64(compressedFile);
        newImages.push(base64);
      }

      setPhotos([...photos, ...newImages]);
      toast.success(`${newImages.length} image(s) uploaded and compressed`);
    } catch (error) {
      toast.error('Failed to process images');
    }
  };

  // Remove image
  const removeImage = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
    toast.success('Image removed');
  };

  // Handle ID document upload
  const handleIdDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      const base64 = await convertToBase64(compressedFile);
      setIdDocumentUrl(base64);
      toast.success('ID document uploaded and compressed');
    } catch (error) {
      toast.error('Failed to process document');
    }
  };

  // Validate step 1
  const validateStep1 = () => {
    if (!gender) {
      toast.error('Please select gender');
      return false;
    }
    if (!dateOfBirth) {
      toast.error('Please enter date of birth');
      return false;
    }
    if (!maritalStatus) {
      toast.error('Please select marital status');
      return false;
    }
    if (!heightFeet || !heightInches) {
      toast.error('Please enter height');
      return false;
    }
    return true;
  };

  // Validate step 2
  const validateStep2 = () => {
    if (!country) {
      toast.error('Please enter country');
      return false;
    }
    if (!state) {
      toast.error('Please enter state');
      return false;
    }
    if (!city) {
      toast.error('Please enter city');
      return false;
    }
    if (!educationLevel) {
      toast.error('Please select education level');
      return false;
    }
    if (!occupation) {
      toast.error('Please enter occupation');
      return false;
    }
    if (!annualIncome) {
      toast.error('Please enter annual income');
      return false;
    }
    if (!religion) {
      toast.error('Please select religion');
      return false;
    }
    return true;
  };

  // Validate step 3
  const validateStep3 = () => {
    // Photos are now optional - users can add them later
    // if (photos.length < 2) {
    //   toast.error('Please upload at least 2 photos');
    //   return false;
    // }

    // ID verification documents are now optional - can be uploaded later
    // if (!idDocumentType) {
    //   toast.error('Please select ID document type');
    //   return false;
    // }
    // if (!idDocumentUrl) {
    //   toast.error('Please upload ID verification document');
    //   return false;
    // }

    // Bio is now optional - users can fill it later if they want
    // if (!bio || bio.trim().length < 50) {
    //   toast.error('Please write a bio (minimum 50 characters)');
    //   return false;
    // }
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep3()) {
      return;
    }

    setIsLoading(true);

    try {
      const profileData = {
        gender,
        dateOfBirth,
        maritalStatus,
        height: parseInt(heightFeet) * 12 + parseInt(heightInches),
        country,
        state,
        city,
        education: {
          level: educationLevel,
          highestDegree: educationLevel
        },
        occupation,
        career: {
          occupation,
          annualIncome
        },
        annualIncome,
        religion,
        bio,
        // Only include profilePhoto if photos are provided
        ...(photos.length > 0 ? { profilePhoto: photos[0] } : {}),
        photos: photos.map((photo, index) => ({
          url: photo,
          isPrimary: index === 0,
          isVerified: false,
          uploadedAt: new Date()
        })),
        // Only include idDocument if both type and url are provided
        ...(idDocumentType && idDocumentUrl ? {
          idDocument: {
            type: idDocumentType,
            url: idDocumentUrl
          }
        } : {}),
        profileCompleted: true
      };

      // Backend only has PUT /profiles/me endpoint which creates OR updates
      const endpoint = `${API_URL}/profiles/me`;

      const response = await axios.put(endpoint, profileData);

      if (response.data.success) {
        toast.success(isResubmission ? 'Profile resubmitted for verification!' : 'Profile submitted for verification!');
        // Reload user data to get updated verification status
        await loadUser();
        // Navigate to pending verification screen
        setTimeout(() => {
          onComplete();
        }, 1000);
      } else {
        toast.error('Failed to submit profile');
        setIsLoading(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit profile');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        {isResubmission && rejectionReason && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">
                  <strong>Profile Rejected:</strong> {rejectionReason}
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Please update your information and resubmit for verification.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isResubmission ? 'Update Your Profile' : 'Complete Your Profile'}
          </h1>
          <p className="text-gray-600">
            {isResubmission
              ? `Step ${step} of 3 - Update your details and resubmit`
              : `Step ${step} of 3 - Fill in your details to get started`
            }
          </p>

          {/* Progress bar */}
          <div className="mt-4 flex gap-2">
            <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-pink-500' : 'bg-gray-200'}`} />
            <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-pink-500' : 'bg-gray-200'}`} />
            <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-pink-500' : 'bg-gray-200'}`} />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="gender">Gender *</Label>
                <div className="mt-2">
                  <SelectablePills
                    options={[
                      { label: 'Male', value: 'male' },
                      { label: 'Female', value: 'female' },
                      { label: 'Other', value: 'other' }
                    ]}
                    value={gender}
                    onChange={setGender}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="maritalStatus">Marital Status *</Label>
                <div className="mt-2">
                  <SelectablePills
                    options={[
                      { label: 'Never Married', value: 'never_married' },
                      { label: 'Divorced', value: 'divorced' },
                      { label: 'Widowed', value: 'widowed' },
                      { label: 'Separated', value: 'separated' }
                    ]}
                    value={maritalStatus}
                    onChange={setMaritalStatus}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-1/2">
                  <Label htmlFor="heightFeet">Height (feet) *</Label>
                  <Input
                    id="heightFeet"
                    type="number"
                    value={heightFeet}
                    onChange={(e) => setHeightFeet(e.target.value)}
                    placeholder="e.g., 5"
                    min="1"
                    max="10"
                  />
                </div>
                <div className="w-1/2">
                  <Label htmlFor="heightInches">Height (inches) *</Label>
                  <Input
                    id="heightInches"
                    type="number"
                    value={heightInches}
                    onChange={(e) => setHeightInches(e.target.value)}
                    placeholder="e.g., 10"
                    min="0"
                    max="11"
                  />
                </div>
              </div>

              <Button
                type="button"
                onClick={handleNext}
                className="w-full bg-pink-500 hover:bg-pink-600"
              >
                Next <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 2: Location & Career */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="country">Country *</Label>
                <select
                  id="country"
                  value={country}
                  onChange={handleCountryChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">Select Country</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  value={state}
                  onChange={handleStateChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">Select State</option>
                  {getAvailableStates().map(s => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="city">City *</Label>
                <select
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">Select City</option>
                  {getAvailableCities().map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="educationLevel">Education Level *</Label>
                <div className="mt-2">
                  <SelectablePills
                    options={[
                      { label: 'High School', value: 'high_school' },
                      { label: "Bachelor's Degree", value: 'bachelors' },
                      { label: "Master's Degree", value: 'masters' },
                      { label: 'PhD', value: 'phd' },
                      { label: 'Diploma', value: 'diploma' },
                      { label: 'Other', value: 'other' }
                    ]}
                    value={educationLevel}
                    onChange={setEducationLevel}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="occupation">Occupation *</Label>
                <Input
                  id="occupation"
                  type="text"
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                  placeholder="e.g., Software Engineer"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="annualIncome">Annual Income *</Label>
                <div className="mt-2">
                  <SelectablePills
                    options={[
                      { label: '₹0 - 3 Lakh', value: '0-3' },
                      { label: '₹3 - 5 Lakh', value: '3-5' },
                      { label: '₹5 - 7 Lakh', value: '5-7' },
                      { label: '₹7 - 10 Lakh', value: '7-10' },
                      { label: '₹10 - 15 Lakh', value: '10-15' },
                      { label: '₹15 - 20 Lakh', value: '15-20' },
                      { label: '₹20 - 30 Lakh', value: '20-30' },
                      { label: '₹30 - 50 Lakh', value: '30-50' },
                      { label: '₹50+ Lakh', value: '50+' }
                    ]}
                    value={annualIncome}
                    onChange={setAnnualIncome}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="religion">Religion *</Label>
                <div className="mt-2">
                  <SelectablePills
                    options={[
                      { label: 'Hindu', value: 'Hindu' },
                      { label: 'Muslim', value: 'Muslim' },
                      { label: 'Christian', value: 'Christian' },
                      { label: 'Sikh', value: 'Sikh' },
                      { label: 'Buddhist', value: 'Buddhist' },
                      { label: 'Jain', value: 'Jain' },
                      { label: 'Other', value: 'Other' }
                    ]}
                    value={religion}
                    onChange={setReligion}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-pink-500 hover:bg-pink-600"
                >
                  Next <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Photos & Bio */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label>Profile Photos (Optional - can be added later)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photos
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    {photos.length}/6 photos uploaded (optional)
                  </p>
                </div>

                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={photo}
                          alt={`Profile ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {index === 0 && (
                          <div className="absolute bottom-2 left-2 px-2 py-1 bg-pink-500 text-white text-xs rounded">
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="idDocumentType">ID Document Type (Optional)</Label>
                <select
                  id="idDocumentType"
                  value={idDocumentType}
                  onChange={(e) => setIdDocumentType(e.target.value)}
                  className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">Select Document Type</option>
                  <option value="aadhar_card">Aadhar Card</option>
                  <option value="passport">Passport</option>
                  <option value="driving_license">Driving License</option>
                  <option value="voter_id">Voter ID</option>
                </select>
              </div>

              <div>
                <Label>ID Verification Document (Optional - can be added later)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIdDocumentUpload}
                    className="hidden"
                    id="id-document-upload"
                  />
                  <label
                    htmlFor="id-document-upload"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </label>
                  {idDocumentUrl && (
                    <p className="text-sm text-gray-500 mt-1">
                      Document uploaded
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    You can upload your verification document later from your profile settings
                  </p>
                </div>

                {idDocumentUrl && (
                  <div className="relative aspect-square mt-4">
                    <img
                      src={idDocumentUrl}
                      alt="ID Document"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setIdDocumentUrl('')}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="bio">About Yourself (Optional)</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Write something about yourself, your interests, what you're looking for in a partner..."
                  rows={5}
                  maxLength={5000}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {bio.length}/5000 characters (optional)
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1"
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-pink-500 hover:bg-pink-600"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {isResubmission ? 'Resubmitting...' : 'Submitting...'}
                    </>
                  ) : (
                    isResubmission ? 'Resubmit for Verification' : 'Submit for Verification'
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}